import { Clear } from '@mui/icons-material';
import {
  Box,
  FormControl,
  IconButton,
  Input,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

const Filters = ({
  search,
  setSearch,
  status,
  setStatus,
  category,
  setCategory,
  categories,
  subcategory,
  setSubcategory,
  subcategories,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
}) => {
  const { t } = useTranslation('translations');

  const STATUS_LABELS = [
    { id: 0, label: t('CreatedOnes') },
    { id: 1, label: t('SignedOnes') },
    { id: 2, label: t('NotSignedOnes') },
    { id: 3, label: t('RejectedOnes') },
    // { id: 4, label: t('ArchiveOnes') },
    { id: 5, label: t('ForExecutionOnes') },
    { id: 6, label: t('CompletedOnes') },
  ];

  const fieldBg = (theme) =>
    theme.palette.mode === 'light'
      ? alpha('#c9c9c9', 0.1)
      : alpha(theme.palette.background.paper, 0.6);

  const stopSelectOpen = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  // ✅ handle category change + reset subcategory
  const handleCategoryChange = (e) => {
    const next = e.target.value === '' ? null : e.target.value;
    setCategory(next);
    setSubcategory(null);
  };

  return (
    <div className="bg-white dark:bg-dark p-20 rounded-xl mb-10 w-full flex flex-wrap gap-x-20 gap-y-10">
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 1.5,
          width: '100%',
        }}
      >
        {/* Search */}
        <Box
          sx={(theme) => ({
            display: 'flex',
            alignItems: 'center',
            flex: 1,
            minWidth: 300,
            px: 1,
            borderRadius: 1.5,
            bgcolor: fieldBg(theme),
            border: `1px solid ${theme.palette.divider}`,
          })}
        >
          <Input
            placeholder={t('Search')}
            disableUnderline
            fullWidth
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            endAdornment={
              search ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearch('')}>
                    <Clear fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null
            }
          />
        </Box>

        {/* Date range */}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ru">
            <DatePicker
              label={t('From')}
              format="DD.MM.YYYY"
              value={startDate ? dayjs(startDate) : null}
              onChange={(v) =>
                v && v.isValid() ? setStartDate(v.format('YYYY-MM-DD')) : setStartDate(null)
              }
              slotProps={{
                textField: {
                  size: 'small',
                  clearable: true,
                  onClear: () => setStartDate(null),
                  sx: (theme) => ({
                    bgcolor: fieldBg(theme),
                    borderRadius: 1.5,
                  }),
                },
              }}
            />
          </LocalizationProvider>

          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ru">
            <DatePicker
              label={t('Until')}
              format="DD.MM.YYYY"
              value={endDate ? dayjs(endDate) : null}
              onChange={(v) =>
                v && v.isValid() ? setEndDate(v.format('YYYY-MM-DD')) : setEndDate(null)
              }
              slotProps={{
                textField: {
                  size: 'small',
                  clearable: true,
                  onClear: () => setEndDate(null),
                  sx: (theme) => ({
                    bgcolor: fieldBg(theme),
                    borderRadius: 1.5,
                  }),
                },
              }}
            />
          </LocalizationProvider>
        </Stack>
      </Box>

      {/* Status */}
      <FormControl
        size="small"
        sx={(theme) => ({
          minWidth: 140,
          bgcolor: fieldBg(theme),
          borderRadius: 1.5,
        })}
      >
        <InputLabel>{t('Status')}</InputLabel>
        <Select
          value={status ?? ''}
          label={t('Status')}
          onChange={(e) => setStatus(e.target.value === '' ? null : e.target.value)}
          displayEmpty
          endAdornment={
            status !== null && status !== '' ? (
              <InputAdornment position="end" sx={{ mr: 0.5 }}>
                <IconButton
                  size="small"
                  onMouseDown={stopSelectOpen}
                  onClick={() => setStatus(null)}
                >
                  <Clear fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null
          }
        >
          {STATUS_LABELS.map((s) => (
            <MenuItem key={s.id} value={s.id}>
              {s.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Category */}
      <FormControl
        size="small"
        sx={(theme) => ({
          minWidth: 140,
          bgcolor: fieldBg(theme),
          borderRadius: 1.5,
        })}
      >
        <InputLabel>{t('Category')}</InputLabel>
        <Select
          value={category ?? ''}
          label={t('Category')}
          onChange={handleCategoryChange}
          displayEmpty
          endAdornment={
            category !== null && category !== '' ? (
              <InputAdornment position="end" sx={{ mr: 0.5 }}>
                <IconButton
                  size="small"
                  onMouseDown={stopSelectOpen}
                  onClick={() => handleCategoryChange({ target: { value: '' } })}
                >
                  <Clear fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null
          }
        >
          {categories?.map((d) => (
            <MenuItem key={d.id} value={d.id}>
              {d.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Subcategory */}
      <FormControl
        size="small"
        disabled={!category} // ✅ disabled until category selected
        sx={(theme) => ({
          minWidth: 140,
          bgcolor: fieldBg(theme),
          borderRadius: 1.5,
          opacity: !category ? 0.6 : 1,
        })}
      >
        <InputLabel>{t('Subcategory')}</InputLabel>
        <Select
          value={subcategory ?? ''}
          label={t('Subcategory')}
          onChange={(e) => setSubcategory(e.target.value === '' ? null : e.target.value)}
          displayEmpty
          endAdornment={
            subcategory !== null && subcategory !== '' && category ? (
              <InputAdornment position="end" sx={{ mr: 0.5 }}>
                <IconButton
                  size="small"
                  onMouseDown={stopSelectOpen}
                  onClick={() => setSubcategory(null)}
                >
                  <Clear fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null
          }
        >
          {subcategories?.map((tp) => (
            <MenuItem key={tp.id} value={tp.id}>
              {tp.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </div>
  );
};

export default Filters;
